
import { useCallback } from 'react'
import { useParams } from '@tanstack/react-router'
import { apiCall } from '@/lib/api/api-helpers'
import { useJobStore } from '@/stores/jobStore'
import { toast } from '@/components/ui/toast'
import { decideGenericJobAdmission, type JobType } from '@genieartinc/schemas'
import type { NodeOrchestratorState } from '@genieartinc/schemas'

interface JobGenerationParams {
  nodeId: string
  nodeType: 'text' | 'image' | 'video'
  purpose?: string
  subPurpose?: string
  /** Canvas family when it is not the generic job node type (shader, 3D, a file node). */
  sourceFamily?: string
  prompt: string
  modelId: string
  settings?: Record<string, unknown>
  variantCount?: number
}

interface UseJobGenerationReturn {
  /** Create a job and return the jobId (or null on failure) */
  submitJob: (params: JobGenerationParams) => Promise<string | null>
}

/**
 * Hook to submit AI generation as an async job.
 *
 * Replaces the synchronous useAIGeneration flow:
 * 1. POST /api/jobs/create → returns jobId immediately (<500ms)
 * 2. Node enters 'queued' state
 * 3. WebSocket events drive progress/completion (handled by useCanvasWebSocket)
 *
 * The caller is responsible for:
 * - Setting orchestratorState to 'queued' after calling submitJob
 * - Handling the result via WebSocket events (useCanvasWebSocket updates jobStore)
 */
export function useJobGeneration(): UseJobGenerationReturn {
  const { id: canvasId } = useParams({ strict: false }) as { id?: string }
  const addJob = useJobStore((s) => s.addJob)

  const submitJob = useCallback(
    async (jobParams: JobGenerationParams): Promise<string | null> => {
      if (!canvasId) return null

      const jobType = resolveJobType(jobParams.nodeType, jobParams.purpose)
      const admission = decideGenericJobAdmission({
        jobType,
        nodeType: jobParams.nodeType,
        purpose: jobParams.purpose,
        subPurpose: jobParams.subPurpose,
        sourceFamily: jobParams.sourceFamily,
        modelId: jobParams.modelId,
      })
      if (!admission.admit) {
        const error = new Error(admission.explanation) as Error & { code: string }
        error.code = admission.code
        throw error
      }

      try {
        const result = (await apiCall('/api/jobs/create', true, {
          method: 'POST',
          body: JSON.stringify({
            type: jobType,
            canvasId,
            nodeId: jobParams.nodeId,
            nodeType: jobParams.nodeType,
            purpose: jobParams.purpose,
            subPurpose: jobParams.subPurpose,
            sourceFamily: jobParams.sourceFamily,
            input: {
              prompt: jobParams.prompt,
              modelId: jobParams.modelId,
              settings: jobParams.settings,
              variantCount: jobParams.variantCount ?? 1,
            },
          }),
        })) as {
          success: boolean
          data: {
            jobId: string
            status: 'PENDING'
            creditsCharged: number
            createdAt: string
            lowBalance?: boolean
            warning?: 'LOW_BALANCE' | 'ZERO_BALANCE'
            remainingBalance?: string
          }
        }

        if (result.success && result.data) {
          addJob({
            id: result.data.jobId,
            type: jobType,
            status: 'PENDING',
            nodeId: jobParams.nodeId,
            canvasId,
            progress: 0,
            createdAt: result.data.createdAt,
          })
          // Trigger immediate balance refresh — credits were just deducted
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('genie:balance-changed'));
          }

          // Show low balance toast after deduction
          if (result.data.warning === 'ZERO_BALANCE') {
            toast('No credits remaining. Top up to keep creating.', { icon: '\u26A0\uFE0F', duration: 6000 })
          } else if (result.data.warning === 'LOW_BALANCE' && result.data.remainingBalance) {
            const bal = parseFloat(result.data.remainingBalance).toFixed(2)
            toast(`Balance running low ($${bal} remaining). Top up to keep creating.`, { icon: '\u26A0\uFE0F', duration: 5000 })
          }

          return result.data.jobId
        }

        return null
      } catch (err) {
        // Re-throw API errors (e.g. INSUFFICIENT_BALANCE) so callers can handle them
        // Only swallow unexpected/network errors
        if (err && typeof err === 'object' && 'code' in err) throw err
        return null
      }
    },
    [canvasId, addJob],
  )

  return { submitJob }
}

/** Map node type + purpose to JobType enum value */
function resolveJobType(nodeType: 'text' | 'image' | 'video', purpose?: string): JobType {
  switch (nodeType) {
    case 'text':
      return 'TEXT_GENERATION'
    case 'image':
      if (purpose === 'edit') return 'IMAGE_TO_IMAGE'
      if (purpose === 'enhance') return 'IMAGE_UPSCALE'
      if (purpose === 'tools') return 'IMAGE_TO_IMAGE'
      return 'TEXT_TO_IMAGE'
    case 'video':
      return 'VIDEO_GENERATION'
    default:
      return 'TEXT_GENERATION'
  }
}

/**
 * Build orchestrator state for 'queued' status.
 * Use this after a successful submitJob call.
 */
export function queuedOrchestratorState(message?: string): NodeOrchestratorState {
  return {
    status: 'queued',
    message: message ?? 'Queued for generation...',
    progress: 0,
  }
}
